import { DestroyRef, Injectable, InjectionToken, inject, signal } from '@angular/core';
import type { HubConnection, IHttpConnectionOptions } from '@microsoft/signalr';
import { AuthHeaderService } from '@neworg-net/amsuite-core/auth';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { Subject } from 'rxjs';
import type { NotifierPing } from './notifications/notification.model';
import { requireHostUrl } from '../shared/required-host-url';

export type NotifierConnectionState = 'disabled' | 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
export type NotifierHubConnection = Pick<HubConnection,
  'start' | 'stop' | 'on' | 'off' | 'onclose' | 'onreconnecting' | 'onreconnected'>;
export type NotifierHubConnectionFactory = (url: string, options: IHttpConnectionOptions) =>
  NotifierHubConnection | Promise<NotifierHubConnection>;

/** This switch affects only push delivery. The persisted REST inbox remains available. */
export const NOTIFIER_REALTIME_ENABLED = new InjectionToken<boolean>('NOTIFIER_REALTIME_ENABLED', {
  providedIn: 'root',
  factory: () => true,
});

export const NOTIFIER_HUB_CONNECTION_FACTORY = new InjectionToken<NotifierHubConnectionFactory>(
  'NOTIFIER_HUB_CONNECTION_FACTORY', {
    providedIn: 'root',
    factory: () => async (url, options) => {
      // Load the transport only when the authenticated session starts it.
      const { HubConnectionBuilder, LogLevel } = await import('@microsoft/signalr');
      return new HubConnectionBuilder()
        .withUrl(url, options)
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        // Access tokens travel in the query string for browser WebSocket/SSE transports.
        // SDK logs must never leak transport URLs into the browser or telemetry.
        .configureLogging(LogLevel.None)
        .build();
    },
  },
);

/** Transport only: REST/store own notification data, unread counts and toast deduplication. */
@Injectable({ providedIn: 'root' })
export class NotifierRealtimeService {
  private readonly auth = inject(AuthHeaderService);
  private readonly config = inject(ConfigService);
  private readonly enabled = inject(NOTIFIER_REALTIME_ENABLED);
  private readonly createConnection = inject(NOTIFIER_HUB_CONNECTION_FACTORY);
  private readonly stateValue = signal<NotifierConnectionState>(this.enabled ? 'disconnected' : 'disabled');
  readonly state = this.stateValue.asReadonly();

  private readonly notificationSubject = new Subject<NotifierPing>();
  private readonly connectedSubject = new Subject<void>();
  private readonly errorSubject = new Subject<void>();
  readonly notificationReceived$ = this.notificationSubject.asObservable();
  readonly connected$ = this.connectedSubject.asObservable();
  /** Deliberately excludes transport errors/URLs which may contain an access token. */
  readonly error$ = this.errorSubject.asObservable();

  private connection: NotifierHubConnection | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryAttempt = 0;
  private active = false;
  private destroyed = false;
  private starting = false;
  private generation = 0;

  private readonly onOnline = (): void => {
    if (!this.active || this.state() === 'connected') return;
    this.clearRetry();
    // Do not wait for an SDK reconnect delay started while the browser was offline.
    if (this.state() === 'reconnecting' || this.starting) this.retireConnection();
    void this.connect();
  };

  private readonly onOffline = (): void => {
    this.clearRetry();
    this.retireConnection();
  };

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.stop();
      this.destroyed = true;
      this.notificationSubject.complete();
      this.connectedSubject.complete();
      this.errorSubject.complete();
    });
  }

  start(): void {
    if (!this.enabled || this.active || this.destroyed) return;
    this.active = true;
    this.generation++;
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    void this.connect();
  }

  stop(): void {
    this.active = false;
    this.retryAttempt = 0;
    this.clearRetry();
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    this.retireConnection();
  }

  private async connect(): Promise<void> {
    if (!this.active || this.starting || navigator.onLine === false) return;
    const generation = this.generation;
    this.starting = true;
    this.stateValue.set('connecting');
    try {
      const connection = this.connection ?? await this.buildConnection(generation);
      if (!this.canConnect(generation)) {
        void connection.stop().catch(() => undefined);
        return;
      }
      await connection.start();
      if (!this.isCurrent(connection, generation)) return;
      this.starting = false;
      this.markConnected();
    } catch {
      if (!this.active || this.generation !== generation) return;
      this.starting = false;
      this.stateValue.set('disconnected');
      this.errorSubject.next();
      // Neither an SDK load failure nor an initial start failure is retried by the SDK.
      this.scheduleRetry();
    }
  }

  private async buildConnection(generation: number): Promise<NotifierHubConnection> {
    const url = `${requireHostUrl(this.config, 'notifier').replace(/\/+$/u, '')}/notifier/v1/hubs/notifications`;
    const connection = await this.createConnection(url, {
      accessTokenFactory: async () => {
        const token = await this.auth.acquireToken();
        if (!this.isCurrent(connection, generation) || !token) {
          throw new Error('Notification authentication unavailable.');
        }
        return token;
      },
    });
    // Logout, destruction and offline/online replacement can retire an SDK load.
    if (!this.canConnect(generation)) return connection;
    this.connection = connection;
    connection.on('notificationReceived', (value: unknown) => {
      if (this.isCurrent(connection, generation) && isNotifierPing(value)) {
        this.notificationSubject.next(value);
      }
    });
    connection.onreconnecting(() => {
      if (this.isCurrent(connection, generation)) this.stateValue.set('reconnecting');
    });
    connection.onreconnected(() => {
      if (this.isCurrent(connection, generation)) this.markConnected();
    });
    connection.onclose(() => {
      if (!this.isCurrent(connection, generation)) return;
      this.stateValue.set('disconnected');
      this.scheduleRetry();
    });
    return connection;
  }

  private markConnected(): void {
    this.clearRetry();
    this.retryAttempt = 0;
    this.stateValue.set('connected');
    this.connectedSubject.next();
  }

  private isCurrent(connection: NotifierHubConnection, generation: number): boolean {
    return this.active && this.connection === connection && this.generation === generation;
  }

  private canConnect(generation: number): boolean {
    return this.active && this.generation === generation && navigator.onLine !== false;
  }

  private scheduleRetry(): void {
    if (!this.active || this.retryTimer !== null || navigator.onLine === false) return;
    const delays = [2000, 5000, 10000, 30000, 60000];
    const delay = delays[Math.min(this.retryAttempt++, delays.length - 1)];
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.connect();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private retireConnection(): void {
    this.generation++;
    const connection = this.connection;
    this.connection = null;
    this.starting = false;
    this.stateValue.set(this.enabled ? 'disconnected' : 'disabled');
    if (connection) {
      connection.off('notificationReceived');
      void connection.stop().catch(() => undefined);
    }
  }
}

function isNotifierPing(value: unknown): value is NotifierPing {
  if (!value || typeof value !== 'object') return false;
  const ping = value as Record<string, unknown>;
  return typeof ping['messageId'] === 'string' && ping['messageId'].length > 0
    && typeof ping['appId'] === 'string' && typeof ping['title'] === 'string'
    && typeof ping['createdAtUtc'] === 'string';
}
